import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth, canManage } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { MapPin, Plus, PencilSimple, Trash, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPES = ["Main Lot", "Service", "Body Shop", "Detail", "Delivery", "Storage", "Offsite", "Sold", "Other"];

export default function Locations() {
  const { user } = useAuth();
  const manage = canManage(user.role);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", type: "Other" });
  const [delId, setDelId] = useState(null);

  const load = () => { setLoading(true); api.get("/locations").then(({ data }) => { setLocations(data); setLoading(false); }); };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm({ name: "", type: "Other" }); setOpen(true); };
  const openEdit = (l) => { setEditing(l); setForm({ name: l.name, type: l.type }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/locations/${editing.id}`, form);
      else await api.post("/locations", form);
      toast.success("Location saved");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async () => {
    try { await api.delete(`/locations/${delId}`); toast.success("Location deleted"); setDelId(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setDelId(null); }
  };

  return (
    <div className="space-y-5" data-testid="locations-page">
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">Locations</h1><p className="text-zinc-500 text-sm mt-1">Manage where your assets live.</p></div>
        {manage && <Button onClick={openNew} className="h-11 gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="add-location"><Plus size={18} weight="bold" /> Add Location</Button>}
      </div>

      {loading ? <div className="flex justify-center py-16"><Spinner size={28} className="animate-spin text-emerald-600" /></div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {locations.map((l) => (
            <div key={l.id} className="p-5 rounded-lg border border-zinc-200 bg-white" data-testid={`location-${l.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-md bg-emerald-50 flex items-center justify-center"><MapPin size={20} weight="bold" className="text-emerald-600" /></div>
                  <div><p className="font-display font-bold">{l.name}</p><p className="text-xs text-zinc-500">{l.type}</p></div>
                </div>
                {manage && (
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(l)} data-testid={`edit-loc-${l.id}`}><PencilSimple size={16} /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => setDelId(l.id)} data-testid={`del-loc-${l.id}`}><Trash size={16} /></Button>
                  </div>
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-zinc-100 flex items-baseline gap-1">
                <span className="font-display text-3xl font-extrabold">{l.asset_count}</span>
                <span className="text-sm text-zinc-500">assets</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Edit Location" : "Add Location"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-sm font-semibold">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid="loc-name" /></div>
            <div><Label className="text-sm font-semibold">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="h-11 mt-1.5" data-testid="loc-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="h-11 border-zinc-300">Cancel</Button>
            <Button onClick={save} className="h-11 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="save-location">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete location?</AlertDialogTitle><AlertDialogDescription>Locations with assigned assets cannot be deleted.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="cancel-delete-location">Cancel</AlertDialogCancel><AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-location">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
